import React, { useEffect, useState, useCallback, memo } from 'react';
import { localizedPath } from '@/lib/localizedPath';
import { useTranslation } from 'react-i18next';
import { getLoginUrl } from '@/const';
import { formatNumber } from '@/utils/formatters';
import { InstallPrompt } from '@/components/InstallPrompt';

/**
 * GameOverDashboard — v3.8 Unified Post-Game Overlay
 *
 * Replaces the three sequential popups (ShareModal, RewardedAdModal, SupporterBadgeModal)
 * with a single high-contrast dashboard. One overlay, one decision point, zero cascade.
 *
 * Layout:
 * ┌─────────────────────────────────────────┐
 * │  GAME OVER          Rank #7 Today ⭐    │
 * │  1,250                                  │
 * │  ─────────────────────────────────────  │
 * │  [     ▶  Play Again (Primary CTA)    ] │
 * │  ─────────────────────────────────────  │
 * │  [🔗 Challenge a friend]  [🪙 Watch & Earn] │
 * │  ─────────────────────────────────────  │
 * │  🫙 Support this game · See Leaderboard  │
 * └─────────────────────────────────────────┘
 */

interface LeaderboardEntry {
  userId?: number;
  name?: string;
  score: number;
}

interface GameOverDashboardProps {
  score: number;
  highScore: number;
  isAuthenticated: boolean;
  isGameOver: boolean;
  topScores: LeaderboardEntry[];
  lastGameSeed?: number;
  onPlayAgain: () => void;
  onWatchAd: () => void;
  /** Total games played — used to decide when to show the install prompt */
  gamesPlayed?: number;
}

function estimateRank(score: number, topScores: LeaderboardEntry[]): number | null {
  if (score <= 0 || topScores.length === 0) return null;
  return topScores.filter((e) => e.score > score).length + 1;
}

function rankBadge(rank: number): { emoji: string; label: string; color: string } {
  if (rank === 1) return { emoji: '🥇', label: '#1 Today', color: '#1a73e8' };
  if (rank <= 3) return { emoji: '🏆', label: `Top 3 Today (#${rank})`, color: '#1a73e8' };
  if (rank <= 10) return { emoji: '⭐', label: `Top 10 Today (#${rank})`, color: '#3b82f6' };
  if (rank <= 50) return { emoji: '🎖️', label: `Top 50 Today (#${rank})`, color: '#6366f1' };
  if (rank <= 100) return { emoji: '🚀', label: `Top 100 Today (#${rank})`, color: '#8b5cf6' };
  return { emoji: '🦕', label: `Rank #${rank} Today`, color: '#6b7280' };
}

export const GameOverDashboard = memo(({
  score,
  highScore,
  isAuthenticated,
  isGameOver,
  topScores,
  lastGameSeed,
  onPlayAgain,
  onWatchAd,
  gamesPlayed = 0,
}: GameOverDashboardProps) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [challengeCopied, setChallengeCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Fade in 300ms after game over
  useEffect(() => {
    if (!isGameOver || score <= 0) { setVisible(false); return; }
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, [isGameOver, score]);

  // Dismiss on Escape
  useEffect(() => {
    if (!visible) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onPlayAgain();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [visible, onPlayAgain]);

  const handleShare = useCallback(() => {
    const text = `I just scored ${score.toLocaleString()} on Chrome Dino Game! Can you beat it? 🦕 ${window.location.origin}`;
    if (navigator.share) {
      navigator.share({ title: 'Chrome Dino Game', text, url: window.location.origin }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      });
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    }
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'share_score', { score, source: 'game_over_dashboard', timestamp: new Date().toISOString() });
    }
  }, [score]);

  const handleChallengeLink = useCallback(() => {
    const url = lastGameSeed
      ? `${window.location.origin}/?seed=${lastGameSeed}`
      : window.location.origin;
    navigator.clipboard.writeText(url).catch(() => {
      const el = document.createElement('textarea');
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setChallengeCopied(true);
    setTimeout(() => setChallengeCopied(false), 2500);
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'share_challenge_link', {
        seed: lastGameSeed, score, source: 'game_over_dashboard',
        timestamp: new Date().toISOString(),
      });
    }
  }, [lastGameSeed, score]);

  const handleSignIn = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'game_over_sign_in_click', {
        score, source: 'game_over_dashboard', timestamp: new Date().toISOString(),
      });
    }
    window.location.href = getLoginUrl('/');
  }, [score]);

  if (!isGameOver || score <= 0 || !visible) return null;

  const rank = estimateRank(score, topScores);
  const { emoji, label, color } = rank ? rankBadge(rank) : { emoji: '🦕', label: 'Score saved locally', color: '#6b7280' };
  const isNewHighScore = score >= highScore && highScore > 0 && score > 0;

  return (
    <>
      <style>{`
        @keyframes dashboardFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dashboardSlideUp {
          from { opacity: 0; transform: scale(0.97) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .god-overlay, .god-card { animation: none !important; }
        }
      `}</style>

      {/* Overlay */}
      <div
        className="god-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="god-title"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          // z-index 65: above GameOverAdBanner (z-50), below SupporterBadge (z-70)
          zIndex: 65,
          padding: '16px',
          animation: 'dashboardFadeIn 200ms cubic-bezier(0.23,1,0.32,1)',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) onPlayAgain(); }}
      >
        {/* Card */}
        <div
          className="god-card"
          style={{
            background: 'var(--bg-color)',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 4px 16px rgba(0,0,0,0.12)',
            fontFamily: 'system-ui,-apple-system,Roboto,Arial,sans-serif',
            animation: 'dashboardSlideUp 220ms cubic-bezier(0.23,1,0.32,1)',
            border: '1px solid var(--border-color)',
          }}
        >
          {/* ── Header ── */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div>
              <p style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ca3af', margin: '0 0 2px' }}>
                {t('game_over.title')}
              </p>
              <p
                id="god-title"
                style={{
                  fontSize: '44px', fontWeight: 800,
                  fontFamily: '"Courier New",Courier,monospace',
                  color: 'var(--text-color)', margin: 0, lineHeight: 1, letterSpacing: '-2px',
                }}
              >
                {formatNumber(score)}
              </p>
              {isNewHighScore && (
                <p style={{ fontSize: '11px', color: '#10b981', fontWeight: 700, margin: '3px 0 0' }}>
                  🎉 {t('game_over.new_high_score')}
                </p>
              )}
            </div>
            {rank && (
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '22px' }}>{emoji}</div>
                <p style={{ fontSize: '11px', fontWeight: 700, color, margin: '2px 0 0', whiteSpace: 'nowrap' }}>
                  {label}
                </p>
              </div>
            )}
          </div>

          {/* ── Primary CTA: Play Again ── */}
          <button
            onClick={onPlayAgain}
            autoFocus
            style={{
              display: 'block', width: '100%', padding: '14px 20px',
              background: '#111827', color: '#ffffff',
              border: 'none', borderRadius: '10px',
              fontSize: '16px', fontWeight: 800, cursor: 'pointer',
              marginBottom: '10px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              transition: 'background 0.15s ease, transform 0.1s ease',
              fontFamily: 'inherit',
              letterSpacing: '0.02em',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#1f2937'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#111827'; }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            aria-label="Play again"
          >
            {t('game_over.play_again')}
          </button>

          {/* ── Secondary CTAs ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            {/* Share Score */}
            <button
              onClick={handleShare}
              style={{
                padding: '10px 12px',
                background: shareCopied ? '#10b981' : 'var(--card-bg)',
                color: shareCopied ? '#ffffff' : 'var(--accent, #1d4ed8)',
                border: `1px solid ${shareCopied ? '#10b981' : 'var(--border-color)'}`,
                borderRadius: '8px',
                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              }}
              aria-label="Share your score"
            >
              {shareCopied ? t('game_over.share_copied') : t('game_over.share')}
            </button>
            {/* Challenge a Friend */}
            <button
              onClick={handleChallengeLink}
              style={{
                padding: '10px 12px',
                background: challengeCopied ? '#10b981' : 'var(--card-bg)',
                color: challengeCopied ? '#ffffff' : 'var(--accent, #166534)',
                border: `1px solid ${challengeCopied ? '#10b981' : 'var(--border-color)'}`,
                borderRadius: '8px',
                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                transition: 'background 0.2s ease',
                fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
              }}
              aria-label="Copy challenge link"
            >
              {challengeCopied ? t('game_over.challenge_copied') : t('game_over.challenge_friend')}
            </button>

            {/* Sign In / Leaderboard — Watch & Earn removed (ads disabled) */}
            {(
              /* Sign In — shown when ads are disabled and user is not authenticated */
              !isAuthenticated ? (
                <button
                  onClick={handleSignIn}
                  style={{
                    padding: '10px 12px',
                    background: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                  }}
                  aria-label="Sign in with Google"
                >
                  <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
                    <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  {t('game_over.sign_in')}
                </button>
              ) : (
                /* Leaderboard link for authenticated users */
                <a
                  href={localizedPath('/leaderboard')}
                  style={{
                    padding: '10px 12px',
                    background: 'var(--card-bg)',
                    color: 'var(--text-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    fontSize: '12px', fontWeight: 700,
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                    textDecoration: 'none',
                  }}
                >
                  {t('game_over.leaderboard')}
                </a>
              )
            )}
          </div>

          {/* ── Install Prompt — show after 3 games ── */}
          <InstallPrompt gamesPlayed={gamesPlayed} />

          {/* ── Footer ── */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', paddingTop: '8px', borderTop: '1px solid var(--border-color)' }}>
            <a
              href="https://tiptopjar.com/chromedinogame"
              target="_blank"
        rel="noopener noreferrer"
              onClick={() => {
                if (typeof window !== 'undefined' && (window as any).trackTipJarClick) {
                  (window as any).trackTipJarClick('game_over_dashboard');
                }
              }}
              style={{ fontSize: '11px', color: '#9ca3af', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
            >
              {t('game_over.support')}
            </a>
            <a
              href={localizedPath('/leaderboard')}
              style={{ fontSize: '11px', color: '#9ca3af', textDecoration: 'none' }}
            >
              {t('game_over.see_leaderboard')} →
            </a>
          </div>
        </div>
      </div>
    </>
  );
});

GameOverDashboard.displayName = 'GameOverDashboard';
